import {
    BackendType,
    FrontendType,
    MultiLangString,
    ObjectType,
    StarjackDateISO,
} from '@/models/general';
import { convertIsoDateFromDate, convertIsoDateToDate } from '@/utils/time';
import { Money } from '@/models/money';
import { Environment } from '@/models/environment';
import { PersonTypeMap } from '@/pages/rulesets/components/ImportedCondtionLinkPersonTypeMapping';

export type BaseCondition<T extends ObjectType = FrontendType> = {
    id?: string;
    name: string;
    externalName: {
        de: string;
        en: string;
    };
    sort: number;
    minDaysBeforeTicketStart?: number | string;
    validFrom?: StarjackDateISO<T>;
    validTo?: StarjackDateISO<T>;
    startDateValidFrom?: StarjackDateISO<T>;
    startDateValidTo?: StarjackDateISO<T>;
    validWeekdays?: T extends FrontendType ? Set<string> : string[];
    startDateValidWeekdays?: T extends FrontendType ? Set<string> : string[];
    personTypeIds: string[];
    sellingGroup?: string;
    notSellingGroup?: string;
    environments: Environment[];
    contingentsProductIdSpecific: boolean;
    userId?: string | null;
    userIds: string[];
    contingentRangeFrom?: StarjackDateISO<T>;
    contingentRangeTo?: StarjackDateISO<T>;
};

export const convertBaseConditionToFrontend = (
    bC: BaseCondition<BackendType>
): BaseCondition => ({
    ...bC,
    validWeekdays: new Set(bC.validWeekdays),
    startDateValidWeekdays: new Set(bC.startDateValidWeekdays),
    validFrom: convertIsoDateToDate(bC.validFrom),
    validTo: convertIsoDateToDate(bC.validTo),
    startDateValidFrom: convertIsoDateToDate(bC.startDateValidFrom),
    startDateValidTo: convertIsoDateToDate(bC.startDateValidTo),
    contingentRangeFrom: convertIsoDateToDate(bC.contingentRangeFrom),
    contingentRangeTo: convertIsoDateToDate(bC.contingentRangeTo),
});

export const convertBaseConditionToBackend = (
    bC: BaseCondition
): BaseCondition<BackendType> => {
    const { id, ...rest } = bC;

    // Check if the id has the 'new-' prefix
    const newIdPrefix = 'new-';
    const shouldSendId = id && !id.startsWith(newIdPrefix);

    return {
        ...(shouldSendId ? { id } : {}), // Only include id if it doesn't have the 'new-' prefix
        ...rest,
        validWeekdays: Array.from(bC.validWeekdays.values()),
        startDateValidWeekdays: Array.from(bC.startDateValidWeekdays.values()),
        validFrom: convertIsoDateFromDate(bC.validFrom),
        validTo: convertIsoDateFromDate(bC.validTo),
        startDateValidFrom: convertIsoDateFromDate(bC.startDateValidFrom),
        startDateValidTo: convertIsoDateFromDate(bC.startDateValidTo),
        contingentRangeFrom: convertIsoDateFromDate(bC.contingentRangeFrom),
        contingentRangeTo: convertIsoDateFromDate(bC.contingentRangeTo),
    };
};

const convertPriceConditionToBackend = (
    pC: PriceCondition
): PriceCondition<BackendType> => ({
    ...(convertBaseConditionToBackend(pC) as PriceCondition<BackendType>),
    priceChange: {
        ...pC.priceChange,
        amount: pC.priceChange?.amount * -1,
    },
});

const convertPriceConditionToFrontend = (
    pC: PriceCondition<BackendType>
): PriceCondition => ({
    ...(convertBaseConditionToFrontend(pC) as PriceCondition),
    priceChange: {
        ...pC.priceChange,
        amount: pC.priceChange.amount * -1,
    },
});

export type ContingentCondition<T extends ObjectType = FrontendType> =
    BaseCondition<T> & {
        maxTicketsPerStartDay: number;
        maxTicketsForDateRange?: number;
        contingentRangeFrom?: StarjackDateISO<T>;
        contingentRangeTo?: StarjackDateISO<T>;
    };

export type PriceCondition<T extends ObjectType = FrontendType> =
    BaseCondition<T> & {
        priceChange: Money;
        priceChangeAbsolute: boolean;
        blockCancellation: boolean;
        dayContingentFrom: number;
        dayContingentTo: number;
        startDateContingentFrom: number;
        startDateContingentTo: number;
        conditionContingentFrom: number;
        conditionContingentTo: number;
        validityPeriodContingentFrom?: number;
        validityPeriodContingentTo?: number;
        calculateRefundableProductVariant: boolean;
        useCatalogPrice: boolean;
        roundTo: number;
        round50: boolean;
    };

export type ConditionLinkPersonType = {
    id: string;
    name: MultiLangString;
};

export type ConditionLink<T extends ObjectType = FrontendType> = {
    id: string;
    name: string;
    personTypes: ConditionLinkPersonType[];
    priceConditions: PriceCondition<T>[];
    contingentConditions: ContingentCondition<T>[];
    imports: {
        id: string;
        name: string;
        personTypeMapping: Record<string, string>;
        missingPersonTypesMapping: string[];
        liftOperatorId: string;
    }[];
    usages: {
        productConfigurationId: string;
        shortProductId: string;
    }[];
};

export type ImportedConditionLink = {
    id: string;
    name: string;
    personTypesMapping: PersonTypeMap;
    missingPersonTypesMapping: ConditionLinkPersonType[];
    original: {
        liftOperatorId: string;
        name: string;
        personTypes: ConditionLink['personTypes'];
    };
    usages: {
        productConfigurationId: string;
        shortProductId: string;
    }[];
};

export type PostImportConditionLinkDTO = {
    conditionLinkId: string;
    name: string;
    personTypesMapping: PersonTypeMap;
};

export const createEmptyConditionLink = (): ConditionLink => ({
    id: '',
    name: '',
    personTypes: [],
    priceConditions: [],
    contingentConditions: [],
    imports: null,
    usages: [],
});

export const createPostImportConditionLinkDTOFromOriginal = (
    original: ConditionLink
): PostImportConditionLinkDTO => ({
    conditionLinkId: original.id,
    name: original.name,
    personTypesMapping: original.personTypes.reduce((acc, curr) => {
        acc[curr.id] = null;
        return acc;
    }, {}),
});

export const convertConditionLinkToFrontend = (
    bCl: ConditionLink<BackendType>
): ConditionLink => ({
    ...bCl,
    priceConditions: bCl.priceConditions.map(convertPriceConditionToFrontend),
    contingentConditions: bCl.contingentConditions.map(
        convertBaseConditionToFrontend
    ) as ContingentCondition[],
});

export const convertConditionLinkToBackend = (
    bCl: ConditionLink
): ConditionLink<BackendType> => ({
    ...bCl,
    priceConditions: bCl.priceConditions.map(convertPriceConditionToBackend),
    contingentConditions: bCl.contingentConditions.map(
        convertBaseConditionToBackend
    ) as ContingentCondition<BackendType>[],
});

export const isOfTypeOriginalConditionLink = (
    toBeDetermined: ImportedConditionLink | ConditionLink
): toBeDetermined is ConditionLink => {
    return (toBeDetermined as ConditionLink).imports !== undefined;
};
